import { useMemo, useState, useEffect } from 'react';
import {
    MaterialReactTable,
    useMaterialReactTable,
} from 'material-react-table';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { GET_INVOICES, MODIFY_INVOICE } from '../../services/graphql';
import SearchIcon from '@mui/icons-material/Search';
import TuneIcon from '@mui/icons-material/Tune';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { getTableDensityStyles, normalizeTableDensity } from '../../config/tableDensity';
import { useSelector } from 'react-redux';

const originalColumns = [
    { accessorKey: 'date', header: 'Date', type: "date", size: 80 },
    { accessorKey: 'invoiceID', header: '#', size: 50 },
    { accessorKey: 'supplierEmail', header: 'Submitted By', size: 180 },
    { accessorKey: 'expenseType', header: 'Expense Type', size: 150 },
    { accessorKey: 'category', header: 'Category', size: 150 },
    { accessorKey: 'subCategory', header: 'Sub-category', size: 150 },
    { accessorKey: 'originalAmount', header: 'Sub Total ($)', size: 150 },
    { accessorKey: 'status', header: 'Status', size: 100 },
    { accessorKey: 'reimbursed', header: 'Paid', size: 120 },
    { accessorKey: 'approverEmails', header: 'Assigned To', size: 150 },
    { accessorKey: 'GST', header: 'GST ($)', size: 80 },
    { accessorKey: 'PST', header: 'PST ($)', size: 80 },
    { accessorKey: 'distance', header: 'Distance', size: 100 },
    { accessorKey: 'project', header: 'Project', size: 100 },
];

const CompanyOverviewTable = ({
    selectedColumns,
    activeTab,
    viewBy,
    showColumnFilters,
    globalFilter,
    onGlobalFilterChange,
    columnOrder,
}) => {
    const navigate = useNavigate();
    const accessLevel = useSelector((state) => state.graphql.accessLevel);
    const isManager = accessLevel === 'Manager';
    const [data, setData] = useState([]);
    const [columnFilters, setColumnFilters] = useState([]);
    const [columnSorts, setColumnSorts] = useState([]);
    const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 5 });
    const [rowCount, setRowCount] = useState();
    // GET_INVOICES doesn't return a totalCount, so rowCount never resolves from
    // undefined - track "have we fetched at least once" separately for the skeleton state.
    const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
    const tableDensity = normalizeTableDensity(localStorage.getItem('tableDensity'));
    const densityStyles = getTableDensityStyles(tableDensity);
    const isDarkMode = useSelector((state) => state.graphql.darkMode);
    const canShowReimbursedColumn = activeTab === 'Approved';
    const canEditPaidDate = canShowReimbursedColumn && isManager;
    const [modifyInvoiceMutation] = useMutation(MODIFY_INVOICE, {
        onError: (err) => {
            console.error('Failed to update reimbursed flag:', err);
        },
    });

    const customIcons = {
        SearchIcon: () => <SearchIcon color='primary' />,
        SearchOffIcon: () => <SearchIcon color='primary' />,
        FilterListIcon: () => <TuneIcon color='primary' />,
    };

    const getServerSortField = (columnId) => {
        if (columnId === 'reimbursed') {
            return 'paidDate';
        }
        return columnId;
    };

    const toBoolean = (value) =>
        value === true || value === 1 || value === '1' || String(value).toLowerCase() === 'true';

    const normalizePaidDate = (value) => {
        const text = String(value || '').trim();
        return text === '' ? null : text.slice(0, 10);
    };

    const formatLocalDate = (date) => {
        if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
            return '';
        }
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const PaidDateCell = ({ row, editable }) => {
        const paidDate = normalizePaidDate(row.original.paidDate);
        const displayDate = paidDate || null;

        if (!editable) {
            return displayDate ? (
                <span>{displayDate}</span>
            ) : (
                <span style={{ opacity: 0.6 }}>Not paid</span>
            );
        }

        return (
            <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                    value={displayDate ? new Date(`${displayDate}T00:00:00`) : null}
                    onChange={(value) => {
                        const nextPaidDate = formatLocalDate(value);
                        handlePaidDateChange(row.original, nextPaidDate);
                    }}
                    closeOnSelect
                    slotProps={{
                        textField: {
                            onClick: (event) => event.stopPropagation(),
                            onDoubleClick: (event) => event.stopPropagation(),
                            size: 'small',
                            variant: 'outlined',
                            placeholder: 'Select date',
                            sx: {
                                width: '100%',
                                minWidth: 132,
                                '& .MuiInputBase-root': {
                                    height: 34,
                                    fontFamily: 'inherit',
                                    fontSize: '0.875rem',
                                    lineHeight: 1.2,
                                },
                                '& .MuiInputBase-input': {
                                    fontFamily: 'inherit',
                                    fontSize: '0.875rem',
                                    lineHeight: 1.2,
                                    padding: '8px 10px',
                                },
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#d4d4d4',
                                },
                            },
                            inputProps: {
                                style: {
                                    cursor: isManager ? 'pointer' : 'not-allowed',
                                },
                            },
                        },
                    }}
                    disabled={!isManager}
                />
            </LocalizationProvider>
        );
    };

    const handlePaidDateChange = async (row, nextPaidDate) => {
        const invoiceID = row.invoiceID;
        const previousReimbursed = toBoolean(row.reimbursed);
        const previousPaidDate = row.paidDate ?? null;
        const normalizedNextPaidDate = normalizePaidDate(nextPaidDate);
        const nextReimbursed = Boolean(normalizedNextPaidDate);

        setData((prev) =>
            prev.map((invoice) =>
                String(invoice.invoiceID) === String(invoiceID)
                    ? {
                        ...invoice,
                        reimbursed: nextReimbursed,
                        paidDate: normalizedNextPaidDate,
                    }
                    : invoice
            )
        );

        const oldValues = {
            reimbursed: previousReimbursed,
            paidDate: previousPaidDate,
        };
        const variables = {
            invoiceID: String(invoiceID),
            originalAmount: String(row.originalAmount ?? '0'),
            currency: String(row.currency ?? 'CAD'),
            CADAmount: row.CADAmount != null
                ? String(row.CADAmount)
                : String(row.originalAmount ?? '0'),
            reimbursed: nextReimbursed,
            oldValues: JSON.stringify(oldValues),
        };

        if (normalizedNextPaidDate) {
            variables.paidDate = normalizedNextPaidDate;
        } else {
            variables.clearPaidDate = true;
        }

        try {
            const response = await modifyInvoiceMutation({ variables });
            const statusCode = response?.data?.modifyInvoice?.status?.statusCode;
            if (statusCode !== '200') {
                throw new Error(
                    response?.data?.modifyInvoice?.body || 'Unable to update paid date.'
                );
            }
        } catch (error) {
            setData((prev) =>
                prev.map((invoice) =>
                    String(invoice.invoiceID) === String(invoiceID)
                        ? {
                            ...invoice,
                            reimbursed: previousReimbursed,
                            paidDate: previousPaidDate,
                        }
                        : invoice
                )
            );
            alert(error?.message || 'Unable to update paid date. Please try again.');
        }
    };

    const columns = useMemo(() => {
        const visibleColumns = originalColumns.filter((column) =>
            selectedColumns.includes(column.accessorKey) &&
            (canShowReimbursedColumn || column.accessorKey !== 'reimbursed')
        );
        const orderSource = (Array.isArray(columnOrder) && columnOrder.length > 0)
            ? columnOrder
            : selectedColumns;
        const orderIndex = new Map(orderSource.map((key, index) => [key, index]));
        visibleColumns.sort(
            (a, b) =>
                (orderIndex.get(a.accessorKey) ?? Number.MAX_SAFE_INTEGER) -
                (orderIndex.get(b.accessorKey) ?? Number.MAX_SAFE_INTEGER)
        );
        return visibleColumns.map((column) => {
            if (column.accessorKey !== 'reimbursed') {
                return column;
            }

            return {
                ...column,
                Cell: ({ row }) => {
                    const paidDate = normalizePaidDate(row.original.paidDate);
                    const displayDate = paidDate || '';

                    if (!canEditPaidDate) {
                        return displayDate ? (
                            <span>{displayDate}</span>
                        ) : (
                            <span style={{ opacity: 0.6 }}>Not paid</span>
                        );
                    }

                    return <PaidDateCell row={row} editable={isManager} />;
                },
            };
        });
    }, [selectedColumns, columnOrder, isManager, canShowReimbursedColumn, canEditPaidDate]);

    const { error, refetch } = useQuery(GET_INVOICES);

    if (error) {
        console.error('Unable to load invoices.', error);
    }

    useEffect(() => {
        let variables = {
            page: pagination.pageIndex + 1,
            pageSize: pagination.pageSize,
            sortBy: columnSorts.length ? getServerSortField(columnSorts[0].id) : null,
            sortOrder: columnSorts.length ? columnSorts[0].desc : null,
            filter: { 
                andFilter: null,
                orFilter: null 
            },
        };

        const filters = [];

        // ========== VIEW BY FILTERS (Date-based) ==========
        const currentDate = new Date();
        const currentYear = currentDate.getFullYear();
        let currentMonth = currentDate.getMonth() + 1;
        const currentDay = currentDate.getDate();

        if (viewBy === 'currentYear') {
            filters.push({
                field: 'date',
                operation: 'contains',
                value: currentYear.toString()
            });
        } else if (viewBy === 'currentMonth') {
            const monthStr = currentMonth < 10 ? '0' + currentMonth : currentMonth.toString();
            const monthPrefix = currentYear + '-' + monthStr + '-';
            filters.push({
                field: 'date',
                operation: 'contains',
                value: monthPrefix
            });
        } else if (viewBy === 'currentPeriod') {
            // current period starts in this month
            if (currentDay >= 15) {
                const monthStr = currentMonth < 10 ? '0' + currentMonth : currentMonth.toString();
                filters.push({
                    field: 'date',
                    operation: 'greater_than_or_equals',
                    value: currentYear + '-' + monthStr + '-' + '15'
                });
            }
            // current period started previous month
            else {
                let prevMonth = currentMonth - 1;
                const prevMonthStr = prevMonth < 10 ? '0' + prevMonth : prevMonth.toString();
                filters.push({
                    field: 'date',
                    operation: 'greater_than_or_equals',
                    value: currentYear + '-' + prevMonthStr + '-' + '15'
                });
            }
        }
        // if viewBy === 'all', don't add any date filter

        // ========== APPROVED / UNAPPROVED TAB FILTERS ==========
        if (activeTab === 'Approved') {
            filters.push({ field: 'status', operation: 'in', value: ['Approved'] });
        } else if (activeTab === 'Unapproved') {
            filters.push({ field: 'status', operation: 'not_in', value: ['Approved'] });
        }
        // if activeTab === 'All', don't add any status filter

        // ========== COLUMN FILTERS ==========
        columnFilters.forEach(f => {
            filters.push({
                field: f.id,
                operation: 'contains',
                value: f.value,
            });
        });

        // Only set andFilter if there are filters
        if (filters.length > 0) {
            variables.filter.andFilter = JSON.stringify(filters);
        }

        // ========== GLOBAL FILTER ==========
        if (globalFilter) {
            const orFilters = columns.map(column => ({
                field: column.accessorKey,
                operation: 'contains',
                value: globalFilter,
            }));
            variables.filter.orFilter = JSON.stringify(orFilters);
        }

        console.log('Refetching with variables:', variables);

        refetch(variables).then(({ data }) => {
            setData(data.getInvoices.body);
            setRowCount(data.getInvoices.totalCount);
            setHasLoadedOnce(true);
        }).catch(error => {
            console.error('Error during refetch:', error);
            setHasLoadedOnce(true);
        });
    }, [
        pagination,
        columnFilters,
        columnSorts,
        globalFilter,
        activeTab,
        viewBy,
        refetch,
        columns,
    ]);

    const table = useMaterialReactTable({
        columns,
        data,
        initialState: { density: densityStyles.density, showColumnFilters: false },
        muiTableHeadRowProps: {
            sx: { backgroundColor: 'var(--theme-bg-elevated)' },
        },
        muiTableHeadCellProps: ({ table }) => ({
            sx: table.getState().density === 'compact'
                ? densityStyles.headCell
                : {},
        }),
        muiTableBodyCellProps: ({ table }) => ({
            sx: table.getState().density === 'compact'
                ? densityStyles.bodyCell
                : {},
        }),
        mrtTheme: {
            baseBackgroundColor: isDarkMode ? '#2a2a2a' : '#ffffff',
        },
        muiTablePaperProps: {
            sx: { backgroundColor: 'var(--theme-bg-elevated)' },
        },
        muiBottomToolbarProps: {
            sx: { backgroundColor: 'var(--theme-bg-elevated)', color: 'var(--theme-text)' },
        },
        enableTopToolbar: false,
        enableColumnResizing: true,
        muiPaginationProps: { color: 'primary', shape: 'rounded', showRowsPerPage: true, variant: 'outlined' },
        icons: customIcons,
        paginationDisplayMode: 'pages',
        enableHiding: false,
        enableFullScreenToggle: false,
        enableDensityToggle: false,
        manualFiltering: true,
        manualPagination: true,
        manualSorting: true,
        rowCount,
        onGlobalFilterChange: onGlobalFilterChange,
        onColumnFiltersChange: setColumnFilters,
        onPaginationChange: setPagination,
        onSortingChange: setColumnSorts,
        state: {
            columnFilters,
            pagination,
            sorting: columnSorts,
            globalFilter,
            showColumnFilters,
            showSkeletons: !hasLoadedOnce,
            columnOrder: columns.map((column) => column.accessorKey),
        },
        muiTableBodyRowProps: ({ row }) => ({
            onDoubleClick: () => {
                const id = row.original['invoiceID'];
                const category = row.original['category'];
                sessionStorage.setItem("selectedInvoiceID", id);
                navigate(`/details/${category}/${id}`);
            },
            sx: {
                cursor: 'pointer',
                backgroundColor: 'var(--theme-bg-elevated)',
                ...densityStyles.bodyRow,
            },
            onTouchEnd: () => {
                const id = row.original['invoiceID'];
                const category = row.original['category'];
                sessionStorage.setItem("selectedInvoiceID", id);
                navigate(`/details/${category}/${id}`);
            },
        }),
    });

    return <MaterialReactTable table={table} />;
};

export default CompanyOverviewTable;
